import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { AppShell } from '@/layouts/AppShell';
import { redirectToPlatformLogin } from '@/services/platform-login';
import { DashboardPage } from '@/modules/dashboard/DashboardPage';
// Leads route removed 2026-05-24 — see src/config/nav.ts comment for why.
// Page code (`src/modules/leads/`) is preserved for easy re-add.
import { CustomerListPage } from '@/modules/customers/CustomerListPage';
import { CustomerProfilePage } from '@/modules/customers/CustomerProfilePage';
import { ConversationInboxPage } from '@/modules/conversations/ConversationInboxPage';
import { ConversationDetailPage } from '@/modules/conversations/ConversationDetailPage';
import { ConversationsGuidePage } from '@/modules/conversations/ConversationsGuidePage';
import { SupportRequestsPage } from '@/modules/support/SupportRequestsPage';
import { SupportRequestDetailPage } from '@/modules/support/SupportRequestDetailPage';
// Tickets route removed 2026-05-24 — Support requests address the purpose;
// every inbound flow already creates a support request. See nav.ts for the
// rationale. Page code (`src/modules/tickets/`) is preserved for revival.
import { ChannelsPage } from '@/modules/channels/ChannelsPage';
import { EmailTemplatesPage } from '@/modules/channels/EmailTemplatesPage';
import { TransactionalEmailTemplatesPage } from '@/modules/email-templates/EmailTemplatesPage';
import { SentEmailsPage } from '@/modules/email-log/SentEmailsPage';
import { ChatbotPage } from '@/modules/chatbot/ChatbotPage';
import { ChatbotGuidePage } from '@/modules/chatbot/ChatbotGuidePage';
import { ChatbotEmbedPage } from '@/modules/chatbot/ChatbotEmbedPage';
import { ChatbotReferencePage } from '@/modules/chatbot/ChatbotReferencePage';
import { ApiKeysPage } from '@/modules/developer/ApiKeysPage';
import { SettingsPage } from '@/modules/settings/SettingsPage';
import { NotFoundPage } from '@/modules/not-found/NotFoundPage';
import { LoginPage } from '@/modules/auth/LoginPage';
import { useAuth } from '@/services/auth-context';

/**
 * Auth-state-aware route gate. Renders:
 *   - a centered spinner while the AuthProvider is rehydrating from
 *     localStorage on boot (avoids a /login flicker for returning users);
 *   - the children when authenticated;
 *   - a redirect to /login (carrying `from` so we can bounce back after
 *     a successful sign-in) when unauthenticated.
 */
function AuthGuard({ children }: { children: React.ReactNode }) {
  const { status } = useAuth();

  // Engage never renders its own login page — when unauthenticated, hard-
  // redirect to the canonical platform login at zafroi.com/login and let
  // the cross-subdomain refresh cookie restore the session on bounce-back.
  useEffect(() => {
    if (status === 'unauthenticated') {
      redirectToPlatformLogin();
    }
  }, [status]);

  if (status === 'loading' || status === 'unauthenticated') {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  return <>{children}</>;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="login" element={<LoginPage />} />

      <Route
        element={
          <AuthGuard>
            <AppShell />
          </AuthGuard>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />

        <Route path="customers">
          <Route index element={<CustomerListPage />} />
          <Route path=":customerId" element={<CustomerProfilePage />} />
        </Route>

        <Route path="conversations">
          <Route index element={<ConversationInboxPage />} />
          {/* `guide` must be declared before `:conversationId` so the static
              path wins over the param match. */}
          <Route path="guide" element={<ConversationsGuidePage />} />
          <Route path=":conversationId" element={<ConversationDetailPage />} />
        </Route>

        <Route path="support">
          <Route index element={<SupportRequestsPage />} />
          <Route path=":requestId" element={<SupportRequestDetailPage />} />
        </Route>

        <Route path="channels">
          <Route index element={<ChannelsPage />} />
          <Route
            path="email/:channelId/templates"
            element={<EmailTemplatesPage />}
          />
        </Route>
        <Route path="chatbot">
          <Route index element={<ChatbotPage />} />
          <Route path="guide" element={<ChatbotGuidePage />} />
          <Route path="embed" element={<ChatbotEmbedPage />} />
          <Route path="reference" element={<ChatbotReferencePage />} />
        </Route>
        <Route path="developer">
          <Route path="api-keys" element={<ApiKeysPage />} />
        </Route>
        <Route path="email-templates" element={<TransactionalEmailTemplatesPage />} />
        <Route path="sent-emails" element={<SentEmailsPage />} />
        <Route path="settings" element={<SettingsPage />} />

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
